import React, { useState } from 'react';
import { isModifiedClick } from '../../utils/navigation';
import {
  Eye,
  Target,
  Award,
  Sparkles,
  ChevronRight,
  Waves,
  HeartHandshake,
  ArrowRight,
  BookOpen,
  Compass,
  CheckCircle2,
  Bookmark,
  Clock,
  Swords,
  Scroll,
  Layers
} from 'lucide-react';

export const AboutPage: React.FC = () => {
  const [activeSymbolCategory, setActiveSymbolCategory] = useState<string>('Semua');
  const [selectedSymbolId, setSelectedSymbolId] = useState<number | null>(null);

  const chapters = [
    {
      id: 'bab-1',
      number: '01',
      badge: 'Permulaan Ilham',
      subtitle: 'Bumi Patani & Alam Semula Jadi',
      title: 'Srikandi Semerah & Lahirnya Gerak Kacak Hati',
      accentColor: 'text-red-600',
      accentBg: 'bg-red-50 border-red-100',
      accentBar: 'bg-red-600',
      quote: '"Alam tidak pernah mengajar kekerasan. Pepohon yang kaku patah dibadai ribut, namun rumput yang meliuk lentur kekal hidup."',
      content: [
        'Kisah persilatan ini tidak bermula di medan perang yang berdarah, tetapi daripada renungan hening terhadap alam ciptaan Ilahi. Di bumi Patani yang masyhur sebagai pusat ketamadunan silat Melayu, seorang srikandi berjiwa waja bernama Semerah sering memerhatikan riak air sungai, liukan ranting pepohon, dan tindak balas haiwan rimba apabila terancam.',
        'Daripada pemerhatian mendalam itu, beliau merumuskan bahawa kekuatan fizikal semata-mata bukanlah penentu kemenangan. Kekuatan sebenar adalah keupayaan menyerap dan membalikkan tenaga lawan. Maka lahirlah gerak tempur defensif yang dinamakan "Kacak Hati" — satu kaedah saintifik yang menanti serangan dengan tenang, mengelak pada saat genting, dan mematikan langkah lawan dalam sekelip mata tanpa mencederakan secara zalim.',
        'Ilmu rahsia kependekaran ini tidak diperturunkan sewenang-wenangnya. Ia diamanahkan dan diijazahkan secara khusus kepada adik angkatnya yang berketurunan bangsawan diraja — seorang pemuda berhati bersih bernama Awang Sulung.'
      ],
      lesson: 'Kekuatan sebenar bertunjangkan kelembutan akal dan sains elakan, bukan keghairahan menyerang.'
    },
    {
      id: 'bab-2',
      number: '02',
      badge: 'Ujian Kependekaran',
      subtitle: 'Medan Cabaran Usia 18 Tahun',
      title: 'Pertarungan Sengit Menundukkan Panglima Raja',
      accentColor: 'text-amber-600',
      accentBg: 'bg-amber-50 border-amber-200/80',
      accentBar: 'bg-amber-500',
      quote: '"Keberanian bukanlah ketiadaan rasa takut, tetapi ketenangan akal apabila berhadapan dengan lawan yang jauh lebih perkasa."',
      content: [
        'Ujian paling getir dalam hidup Awang Sulung tiba sewaktu beliau baru berusia 18 tahun. Gelaran "Pendekar Muda" yang disandangnya telah mengundang rasa cemburu seorang Panglima Raja yang tersohor dengan tubuh sasa, kudrat kebal, dan ilmu tempur istana yang digeruni ramai.',
        'Panglima tersebut yang sombong dan mendabik dada secara terbuka mencabar Awang Sulung bertarung di hadapan khalayak ramai dengan niat memperlecehkan ilmu yang dipelajarinya daripada seorang srikandi wanita.',
        'Di tengah gelanggang, sang panglima meluru dengan tumbukan padu dan tetakan bertubi-tubi bagai banteng garang. Namun dengan disiplin gerak Kacak Hati, Awang Sulung bergerak bagai bayang-bayang di atas air — tidak menyambut kekerasan dengan kekerasan, sebaliknya meliuk santun mengikut arus serangan. Sebaik panglima hilang imbangan, Awang Sulung mengunci pergelangan sendinya lalu merebahkan tokoh perkasa itu ke tanah dalam satu kilasan pantas.'
      ],
      lesson: 'Ketangkasan elakan dan ketenangan jiwa mampu mematahkan kekuatan otot yang paling digeruni.'
    },
    {
      id: 'bab-3',
      number: '03',
      badge: 'Detik Penyatuan',
      subtitle: 'Kemaafan Yang Menundukkan Ego',
      title: 'Lahirnya Falsafah: "Biduk Berlalu, Kiambang Bertaut"',
      accentColor: 'text-blue-600',
      accentBg: 'bg-blue-50 border-blue-100',
      accentBar: 'bg-blue-600',
      quote: '"Sengketa hanyalah biduk yang melintas seketika. Sebaik ia berlalu, rumpun kiambang kembali bertaut rapat tanpa meninggalkan parut."',
      content: [
        'Saat sang panglima rebah tersungkur, suasana gelanggang hening terpaku. Orang ramai menjangkakan anak muda belasan tahun itu akan bersorak bangga, menghina, atau memijak maruah lawannya yang telah tewas.',
        'Namun di saat itulah keagungan peribadi pendekar sejati terukir. Awang Sulung tidak mendabik dada sedikit pun. Beliau melutut, menghulurkan kedua-dua belah tangannya dengan penuh hormat, membantu panglima itu bangkit, lalu memohon kemaafan di hadapan semua saksi.',
        'Tindakan luhur itu meluluhkan hati sang panglima yang akhirnya memeluk erat Awang Sulung sambil menitiskan air mata keinsafan. Peristiwa inilah yang melahirkan peribahasa keramat perguruan: "Biduk Berlalu, Kiambang Bertaut" — bahawa permusuhan hanyalah perahu yang membelah air untuk seketika; apabila ia berlalu, kasih persaudaraan bertaut kembali lebih erat dari sebelumnya.',
        'Bagi mengabadikan detik suci ini, aliran persilatan ini dimasyhurkan dengan nama "Awang Sulung Merah Muda" — menyatukan keluhuran hati Awang Sulung bersama kehebatan Srikandi Semerah.'
      ],
      lesson: 'Kemenangan teragung seorang pendekar bukanlah menewaskan lawan, tetapi menundukkan egonya sendiri dan memaafkan.'
    },
    {
      id: 'bab-4',
      number: '04',
      badge: 'Pegangan Abadi',
      subtitle: 'Amanah Buat Generasi Masa Kini',
      title: '"Silat Itu Mencari Kawan, Bukan Menempah Lawan"',
      accentColor: 'text-slate-900',
      accentBg: 'bg-slate-100 border-slate-200',
      accentBar: 'bg-slate-900',
      quote: '"Makin tunduk makin berisi — resam padi yang menjadi pakaian batin setiap insan yang bernaung di bawah panji ASMM."',
      content: [
        'Kini, berdekad lamanya setelah peristiwa itu berlalu, amanah Pendekar Awang Sulung Merah Muda terus hidup dan berdenyut dalam dada setiap pesilat di gelanggang latihan kami di seluruh Kedah.',
        'Kami tidak melatih generasi muda untuk menjadi samseng jalanan atau jaguh sombong. Sukatan bengkung daripada Hitam, Putih, Merah, sehingga Kuning dan Biru Samudera sentiasa menekankan bahawa semakin tinggi ilmu di dada, semakin tunduk diri kepada Sang Pencipta dan masyarakat.',
        'Inilah identiti sejati ASMM: Sebuah persaudaraan berakar dari nilai kemaafan, berdisiplin tinggi, dan teguh memartabatkan pusaka bangsa demi kesejahteraan ummah.'
      ],
      lesson: 'Silat adalah perisai pertahanan diri dan jambatan kasih sayang, bukan alat persengketaan.'
    }
  ];

  const allSymbols = [
    {
      id: 1,
      category: 'Warisan & Sejarah',
      title: 'Tulisan Jawi',
      tag: 'Khat Pusaka',
      meaning: 'Menterjemahkan nama mulia "Awang Sulung" di bahagian atas dan "Merah Muda" di bahagian bawah, melambangkan asal-usul warisan persuratan tradisi Melayu Islam.',
      accentBorder: 'border-amber-400',
      accentBg: 'bg-amber-50/60',
      badgeColor: 'bg-amber-500 text-slate-950'
    },
    {
      id: 2,
      category: 'Warisan & Sejarah',
      title: 'Kapal Perang Siam',
      tag: 'Pusat Salasilah',
      meaning: 'Silat ini berasal dan berkembang dari Siam Patani — pusat utama permulaan dan penyebaran ilmu seni silat Melayu rantau ini.',
      accentBorder: 'border-slate-300',
      accentBg: 'bg-slate-50',
      badgeColor: 'bg-slate-900 text-white'
    },
    {
      id: 3,
      category: 'Sains & Disiplin',
      title: 'Lautan Membiru',
      tag: 'Ilmu Tanpa Hujung',
      meaning: 'Membawa maksud ilmu persilatan, seni tempur fizikal, dan kebatinan yang dituntut tiada noktah penyudahnya. Semakin diteroka, semakin luas rahsianya.',
      accentBorder: 'border-blue-400',
      accentBg: 'bg-blue-50/60',
      badgeColor: 'bg-blue-600 text-white'
    },
    {
      id: 4,
      category: 'Sains & Disiplin',
      title: 'Tangkai Padi',
      tag: 'Resmi Padi & Taat',
      meaning: 'Menuntut ilmu persilatan harus mengikut resmi padi — makin tunduk makin berisi. Ia juga lambang taat setia kepada raja dan pemerintah negeri.',
      accentBorder: 'border-amber-400',
      accentBg: 'bg-amber-50/60',
      badgeColor: 'bg-amber-500 text-slate-950'
    },
    {
      id: 5,
      category: 'Sains & Disiplin',
      title: 'Selendang',
      tag: 'Amanah & Jihad',
      meaning: 'Amanah keilmuan yang dipikul di bahu harus dipelihara sebaik-baiknya, dan menuntut silat merupakan satu bentuk jihad fisabilillah mempertahankan diri dan maruah.',
      accentBorder: 'border-red-400',
      accentBg: 'bg-red-50/60',
      badgeColor: 'bg-red-600 text-white'
    },
    {
      id: 6,
      category: 'Sains & Disiplin',
      title: 'Bukit-bukau Berpuncak Tajam',
      tag: 'Ketajaman Akal & Gerak',
      meaning: 'Ilmu persilatan jika sentiasa dilatih dan diguna akan menjadi semakin tajam, pantas, dan jitu seumpama mata pedang kependekaran.',
      accentBorder: 'border-slate-400',
      accentBg: 'bg-slate-50',
      badgeColor: 'bg-slate-800 text-white'
    },
    {
      id: 7,
      category: 'Sains & Disiplin',
      title: 'Awan Putih',
      tag: 'Perlindungan & Kasih',
      meaning: 'Silat sebagai naungan pelindung kepada sesiapa yang memerlukan bantuan, mendidik setiap pesilat menghulurkan pertolongan ketika sesama insan dilanda musibah.',
      accentBorder: 'border-slate-200',
      accentBg: 'bg-white',
      badgeColor: 'bg-slate-100 text-slate-800 border border-slate-200'
    },
    {
      id: 8,
      category: 'Warna Perjuangan',
      title: 'Warna Kuning (Emas Diraja)',
      tag: 'Keturunan Diraja',
      meaning: 'Melambangkan Awang Sulung berasal daripada titisan keturunan diraja berdaulat serta memancarkan kemuliaan budi pekerti anak bangsawan.',
      accentBorder: 'border-amber-500',
      accentBg: 'bg-amber-50/80',
      badgeColor: 'bg-amber-500 text-slate-950'
    },
    {
      id: 9,
      category: 'Warna Perjuangan',
      title: 'Warna Merah (Darah Pendekar)',
      tag: 'Keberanian Perwira',
      meaning: 'Darah kependekaran yang gagah, berani, dan pantang berundur dalam menegakkan kebenaran serta menentang kezaliman.',
      accentBorder: 'border-red-500',
      accentBg: 'bg-red-50/80',
      badgeColor: 'bg-red-600 text-white'
    },
    {
      id: 10,
      category: 'Warna Perjuangan',
      title: 'Warna Biru (Lautan Membiru)',
      tag: 'Ukhuwah Perpaduan',
      meaning: 'Tali perpaduan kukuh dalam kalangan pesilat tanpa mengira darjat, keturunan, mahupun latar belakang — bersatu hati di bawah satu bumbung perguruan.',
      accentBorder: 'border-blue-500',
      accentBg: 'bg-blue-50/80',
      badgeColor: 'bg-blue-600 text-white'
    },
    {
      id: 11,
      category: 'Warna Perjuangan',
      title: 'Warna Putih (Kesucian Hati)',
      tag: 'Niat & Ikhlas',
      meaning: 'Kesucian hati dan keikhlasan niat. Menuntut silat wajib bermula daripada jiwa yang bersih, bebas daripada niat sombong, takbur, atau dengki.',
      accentBorder: 'border-slate-300',
      accentBg: 'bg-white',
      badgeColor: 'bg-white text-slate-900 border border-slate-300'
    },
    {
      id: 12,
      category: 'Warna Perjuangan',
      title: 'Warna Hitam (Misteri Pendekar)',
      tag: 'Rahsia Keilmuan',
      meaning: 'Rahsia dan misteri ilmu kependekaran perguruan yang terpelihara rapi, hanya dibuka kepada mereka yang telah terbukti setia dan beradab luhur.',
      accentBorder: 'border-slate-900',
      accentBg: 'bg-slate-100',
      badgeColor: 'bg-slate-900 text-white'
    }
  ];

  const filteredSymbols = activeSymbolCategory === 'Semua'
    ? allSymbols
    : allSymbols.filter((s) => s.category === activeSymbolCategory);

  const scrollToStory = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavigate = (e: React.MouseEvent, path: string) => {
    if (isModifiedClick(e)) return;
    e.preventDefault();
    window.history.pushState(null, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="w-full space-y-16 min-h-[75vh] bg-white text-slate-900 pb-20">
      {/* 1. Masterpiece Editorial Hero Header (The Hook & Prologue) */}
      <section className="relative text-center pt-8 sm:pt-14 pb-4">
        {/* Floating Reading Tracker Capsule */}
        <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-slate-50 border border-slate-200/80 text-xs font-bold text-slate-600 mb-6 shadow-2xs">
          <Clock className="w-3.5 h-3.5 text-red-600" />
          <span>Hikayat Perguruan • Anggaran Bacaan 3 Minit</span>
          <span className="h-3 w-px bg-slate-200" />
          <span className="text-amber-700 font-extrabold">4 Babak Epik</span>
        </div>

        {/* Display-XL Main Literary Headline */}
        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight max-w-4xl mx-auto leading-tight sm:leading-tight">
          Dua Nama, Satu Jiwa: <br className="hidden sm:block" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-amber-500 to-red-700">
            Hikayat Awang Sulung & Srikandi Semerah
          </span>
        </h1>

        {/* Intriguing Subtitle to Reel the Reader In */}
        <p className="text-sm sm:text-base lg:text-lg text-slate-600 font-medium mt-6 max-w-3xl mx-auto leading-relaxed">
          Bukan calang-calang aliran silat yang lahir daripada kemaafan, bukan dendam. Menyusuri bagaimana rahsia gerak <span className="font-bold text-slate-900">Kacak Hati</span> dan kerendahan budi seorang pendekar muda berusia 18 tahun menundukkan keangkuhan seorang panglima raja — lalu mengabadikan pepatah agung <span className="italic font-bold text-slate-900">&ldquo;Biduk Berlalu, Kiambang Bertaut&rdquo;</span>.
        </p>

        {/* Floating Narrative Roadmap Quick-Jumps */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-8 max-w-3xl mx-auto">
          {chapters.map((c) => (
            <button
              key={c.id}
              onClick={() => scrollToStory(c.id)}
              className="px-3.5 py-1.5 rounded-full text-xs font-bold bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 shadow-2xs transition-all hover:border-slate-300 flex items-center gap-1.5 cursor-pointer"
            >
              <span className="text-red-600 font-black">{c.number}.</span>
              <span>{c.badge}</span>
            </button>
          ))}
          <button
            onClick={() => scrollToStory('maksud-lambang')}
            className="px-3.5 py-1.5 rounded-full text-xs font-bold bg-slate-900 hover:bg-slate-800 text-white shadow-xs transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <span>12 Lambang</span>
            <ChevronRight className="w-3.5 h-3.5 text-amber-400" />
          </button>
        </div>

        {/* Hero Visual Anchor Badge */}
        <div className="mt-12 max-w-2xl mx-auto relative pt-8">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full ring-4 ring-white shadow-md bg-white p-2.5 flex items-center justify-center">
              <img
                src="/logo-pssasmm.png"
                alt="PSSASMM"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-6 sm:p-8 pt-14 sm:pt-16 shadow-sm text-center">
            <span className="text-[11px] font-bold text-amber-600 uppercase tracking-widest block">
              Teras Keperibadian Pesilat
            </span>
            <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 leading-snug mt-1">
              "Silat Mencari Kawan, Bukan Menempah Lawan"
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 italic leading-relaxed mt-2 max-w-lg mx-auto">
              "Ilmu persilatan Awang Sulung Merah Muda tidak dicipta untuk membinasakan maruah manusia, tetapi untuk memelihara kedamaian, membela yang lemah, dan menyatukan persaudaraan."
            </p>
          </div>
        </div>
      </section>

      {/* 2. The 4-Act Literary Narrative Journey (Page-Turner Editorial Flow) */}
      <section className="space-y-12 max-w-4xl mx-auto">
        <div className="text-center space-y-1">
          <span className="text-xs font-bold uppercase tracking-widest text-red-600">
            Kisah Sebenar Di Sebalik Nama
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
            Empat Babak Lahirnya Perguruan
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 max-w-md mx-auto">
            Hayati setiap detik pertemuan yang melahirkan aliran seni silat warisan asli ini.
          </p>
        </div>

        <div className="space-y-10">
          {chapters.map((chap, idx) => (
            <article
              key={chap.id}
              id={chap.id}
              className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 sm:p-10 relative overflow-hidden transition-all duration-300 hover:shadow-md group"
            >
              {/* Left Vertical Accent Line */}
              <div className={`absolute top-0 left-0 bottom-0 w-2 ${chap.accentBar}`} />

              <div className="space-y-6">
                {/* Chapter Meta Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight font-mono">
                      {chap.number}
                    </span>
                    <div>
                      <span className={`inline-block text-[11px] font-extrabold px-2.5 py-0.5 rounded-full border ${chap.accentBg} ${chap.accentColor}`}>
                        {chap.badge}
                      </span>
                      <span className="text-xs text-slate-400 font-semibold ml-2">
                        {chap.subtitle}
                      </span>
                    </div>
                  </div>
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    Babak {idx + 1} daripada 4
                  </span>
                </div>

                {/* Chapter Title */}
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-black text-slate-900 tracking-tight leading-tight">
                  {chap.title}
                </h3>

                {/* Editorial Pull-Quote Callout */}
                <blockquote className="p-4 sm:p-5 rounded-2xl bg-slate-50/80 border-l-4 border-slate-900 text-xs sm:text-sm font-semibold italic text-slate-700 leading-relaxed">
                  {chap.quote}
                </blockquote>

                {/* Narrative Body Paragraphs */}
                <div className="space-y-3.5 text-xs sm:text-sm text-slate-600 leading-relaxed font-medium">
                  {chap.content.map((p, pIdx) => (
                    <p key={pIdx}>{p}</p>
                  ))}
                </div>

                {/* Moral Lesson Footer Capsule */}
                <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100 flex items-start sm:items-center gap-3">
                  <CheckCircle2 className={`w-4 h-4 shrink-0 ${chap.accentColor}`} />
                  <div className="text-xs text-slate-700 font-bold leading-snug">
                    <span className="text-slate-400 font-normal uppercase text-[10px] block sm:inline sm:mr-1.5">
                      Intipati Hikayat:
                    </span>
                    {chap.lesson}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* 3. The 12 Symbols Interactive Codex (Bedah Rahsia Lambang) */}
      <section id="maksud-lambang" className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 sm:p-10 space-y-8 max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto space-y-1.5">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-800 text-xs font-bold border border-amber-200">
            <Award className="w-3.5 h-3.5 text-amber-600" />
            <span>Koleksi Rahsia Lambang</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
            Bedah Falsafah 12 Rahsia Lambang ASMM
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 font-medium">
            Setiap garisan, ukiran, dan warna pada lencana rasmi perguruan merakamkan amanah salasilah dan kod etika pendekar.
          </p>
        </div>

        {/* Category Filter Pills */}
        <div className="flex flex-wrap items-center justify-center gap-2">
          {['Semua', 'Warisan & Sejarah', 'Sains & Disiplin', 'Warna Perjuangan'].map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveSymbolCategory(cat)}
              className={`px-4 py-2 rounded-full text-xs font-bold transition-all cursor-pointer ${activeSymbolCategory === cat
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-slate-100/80 hover:bg-slate-200/60 text-slate-700'
                }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Central Logo Spotlight with Rotating Ring */}
        <div className="flex flex-col items-center justify-center pt-2 pb-4">
          <div className="relative flex items-center justify-center w-48 h-48 sm:w-56 sm:h-56">
            <div className="absolute inset-0 rounded-full border border-dashed border-slate-200 animate-[spin_180s_linear_infinite]" />
            <div className="absolute inset-2 rounded-full border border-slate-100" />
            <div className="relative w-36 h-36 sm:w-44 sm:h-44 bg-white rounded-full shadow-lg border border-slate-100 p-5 flex items-center justify-center z-10 transition-transform hover:scale-105 duration-300">
              <img
                src="/logo-pssasmm.png"
                alt="Lencana ASMM"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider mt-3">
            Lencana Rasmi Persatuan Seni Silat Awang Sulung Merah Muda
          </span>
        </div>

        {/* Grid of Symbol Codex Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSymbols.map((item) => {
            const isSelected = selectedSymbolId === item.id;
            return (
              <div
                key={item.id}
                onClick={() => setSelectedSymbolId(isSelected ? null : item.id)}
                className={`p-5 rounded-2xl border transition-all duration-200 cursor-pointer flex flex-col justify-between space-y-3 ${isSelected
                    ? 'border-red-600 ring-2 ring-red-600/20 shadow-md bg-white'
                    : `${item.accentBg} ${item.accentBorder} hover:shadow-sm hover:border-slate-300`
                  }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={`w-7 h-7 rounded-lg text-xs font-black flex items-center justify-center shrink-0 ${item.badgeColor}`}>
                    {item.id}
                  </span>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    {item.tag}
                  </span>
                </div>

                <div className="space-y-1">
                  <h4 className="font-extrabold text-sm text-slate-900 leading-snug">
                    {item.title}
                  </h4>
                  <p className="text-xs text-slate-600 leading-relaxed font-medium">
                    {item.meaning}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 4. Visi & Misi Persatuan (The Strategic Mandate) */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
        {/* Visi */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 sm:p-8 space-y-4 hover:shadow-md transition-all">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-100 text-amber-600 flex items-center justify-center shrink-0">
              <Eye className="w-7 h-7" />
            </div>
            <div>
              <span className="text-[10px] text-amber-600 font-extrabold uppercase tracking-widest block">
                Matlamat Utama
              </span>
              <h3 className="text-lg sm:text-xl font-black text-slate-900">
                Visi Persatuan
              </h3>
            </div>
          </div>
          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-medium pt-1">
            Memartabatkan seni silat warisan bangsa Pendekar Awang Sulung Merah Muda sebagai medium pembinaan jati diri, adab kependekaran, dan sahsiah terpuji dalam kalangan belia dan remaja, sekali gus melahirkan pesilat bertaraf juara di persada kebangsaan dan antarabangsa.
          </p>
        </div>

        {/* Misi */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 sm:p-8 space-y-4 hover:shadow-md transition-all">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 text-red-600 flex items-center justify-center shrink-0">
              <Target className="w-7 h-7" />
            </div>
            <div>
              <span className="text-[10px] text-red-600 font-extrabold uppercase tracking-widest block">
                Langkah Strategik
              </span>
              <h3 className="text-lg sm:text-xl font-black text-slate-900">
                Misi Persatuan
              </h3>
            </div>
          </div>
          <ul className="space-y-2 pt-1 text-xs sm:text-sm text-slate-600 leading-relaxed font-medium">
            <li className="flex items-start gap-2.5">
              <span className="w-2 h-2 rounded-full bg-red-600 mt-1.5 shrink-0" />
              <span><strong>Pengembangan Warisan:</strong> Mengembang dan memajukan seni silat warisan Pendekar Awang Sulung Merah Muda secara sistematik dan cekap.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="w-2 h-2 rounded-full bg-red-600 mt-1.5 shrink-0" />
              <span><strong>Pembangunan Belia:</strong> Menarik dan membimbing generasi muda mewarisi kebudayaan persilatan bagi membendung gejala sosial.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="w-2 h-2 rounded-full bg-red-600 mt-1.5 shrink-0" />
              <span><strong>Kecemerlangan Sukan:</strong> Melatih dan melahirkan atlet-atlet yang berwibawa tinggi untuk kejuaraan silat seni dan olahraga.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="w-2 h-2 rounded-full bg-red-600 mt-1.5 shrink-0" />
              <span><strong>Perpaduan & Sahsiah:</strong> Menyemai semangat setia kawan, hormat-menghormati, dan nilai patriotisme tinggi terhadap tanah air.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* 5. Masterpiece Epilogue & High-Intent Action Banner */}
      <section className="bg-white rounded-3xl border border-slate-100 p-6 sm:p-10 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left max-w-5xl mx-auto">
        <div className="space-y-1.5 max-w-xl">
          <span className="inline-block px-3 py-1 rounded-full bg-red-50 text-red-700 text-xs font-bold border border-red-100">
            Langkah Permulaan Pendekar
          </span>
          <h3 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
            Bersedia Mewarisi Falsafah Ini?
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 font-medium leading-relaxed">
            Sertai keluarga besar Persatuan Seni Silat Awang Sulung Merah Muda. Pilih gelanggang latihan terdekat anda atau terokai kurikulum silibus bengkung rasmi.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
          <a
            href="/hubungi"
            onClick={(e) => handleNavigate(e, '/hubungi')}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold text-xs sm:text-sm rounded-full shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 flex items-center gap-2 cursor-pointer"
          >
            <span>Daftar Gelanggang</span>
            <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href="/silibus-awam"
            onClick={(e) => handleNavigate(e, '/silibus-awam')}
            className="px-6 py-3 bg-white hover:bg-slate-50 text-slate-900 font-bold text-xs sm:text-sm rounded-full border border-slate-200 shadow-xs transition flex items-center gap-2 cursor-pointer"
          >
            <BookOpen className="w-4 h-4 text-slate-700" />
            <span>Lihat Silibus</span>
          </a>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
